import { Link } from "react-router-dom";
import { STORY, ROLE_CARDS } from "@/data/core";
import { IllustrativeBadge } from "@/components/IllustrativeBadge";
import { ArrowRight, ArrowUpRight, Lock, Gavel, FileCheck2, CheckCircle2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1762416964549-c69d31e8c7a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxjb21tdW5pdHklMjBodWIlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODMzNDMwMzN8MA&ixlib=rb-4.1.0&q=85&w=1600";

const SUCCESS = [
  "I know what each person has to do.",
  "I know what information is private and what is shared.",
  "I know who can approve what.",
  "I can see how a room request is managed without emails and confusion.",
  "I can see why a future wallet and receipt layer may be useful.",
];

const ACCENT = { moss: "bg-[#E8F0EA] text-mill-moss", blue: "bg-[#DBEAFE] text-katlas-blue", receipt: "bg-[#FEF3C7] text-[#B45309]" };

export default function CoreLanding() {
  return (
    <div className="pb-8">
      <section className="relative overflow-hidden border-b border-stone-200">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-mill-bg via-mill-bg/95 to-mill-bg/70" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <div className="max-w-3xl">
            <IllustrativeBadge className="mb-5" />
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-mill-ink sm:text-5xl lg:text-6xl">
              A clear role-based operating relationship
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-mill-muted">
              One end-to-end story showing how The Mill can give each person a clear picture of their responsibilities,
              permissions, data custody, consent choices — and the evidence created when meaningful actions complete.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/staff" className="inline-flex items-center gap-2 rounded-md bg-mill-moss px-5 py-3 text-sm font-semibold text-white hover:bg-mill-moss-dark" data-testid="launch-admin">
                Open the staff workspace <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/" className="inline-flex items-center gap-2 rounded-md border border-stone-300 bg-white px-5 py-3 text-sm font-semibold text-mill-ink hover:bg-mill-alt" data-testid="launch-booking">
                Back to The Mill home
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {/* The one story */}
        <section className="py-12" data-testid="story-section">
          <Head eyebrow="The proof" title="One operational story, six handovers" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {STORY.map((s, i) => (
              <div key={s.n} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`story-${s.n}`}>
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-mill-moss font-display text-sm font-bold text-white">{s.n}</span>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-mill-moss">{s.who}</span>
                </div>
                <h3 className="mt-3 font-display text-base font-semibold text-mill-ink">{s.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-mill-muted">{s.detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Roles: five questions */}
        <section className="py-12" data-testid="roles-section">
          <Head eyebrow="The role engagement model" title="Every role answers five questions" />
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {ROLE_CARDS.map((r) => (
              <div key={r.id} className="rounded-2xl border border-stone-200 bg-white p-6" data-testid={`role-${r.id}`}>
                <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${ACCENT[r.accent]}`}>{r.label}</span>
                <dl className="mt-4 space-y-3 text-sm">
                  <QA q="Who am I?" a={r.who} />
                  <QA q="What do I hold or provide?" a={r.holds} />
                  <QA q="What may I see or do?" a={r.can} />
                  <QA q="Who approves or responds?" a={r.approves} />
                  <QA q="What evidence is created?" a={r.evidence} />
                </dl>
              </div>
            ))}
          </div>
        </section>

        {/* CAROPS explainer */}
        <section className="py-12" data-testid="carops-explainer">
          <Head eyebrow="Visible at every stage" title="Custody · Authority · Evidence" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[
              { icon: Lock, t: "Custody", d: "Who holds the underlying information at each step.", tone: "text-mill-moss" },
              { icon: Gavel, t: "Authority", d: "Who may approve, activate, amend or decide.", tone: "text-katlas-blue" },
              { icon: FileCheck2, t: "Evidence", d: "What record proves the step happened.", tone: "text-[#B45309]" },
            ].map((c) => (
              <div key={c.t} className="rounded-2xl border border-stone-200 bg-white p-6">
                <c.icon className={`h-6 w-6 ${c.tone}`} />
                <h3 className="mt-3 font-display text-lg font-semibold text-mill-ink">{c.t}</h3>
                <p className="mt-1 text-sm text-mill-muted">{c.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Success criterion */}
        <section className="pb-6" data-testid="success-section">
          <div className="rounded-3xl border border-stone-200 bg-mill-alt/50 p-7 sm:p-10">
            <Head eyebrow="Under five minutes" title="A stakeholder should be able to say:" />
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {SUCCESS.map((q) => (
                <li key={q} className="flex items-start gap-2.5 rounded-xl bg-white p-4 text-sm text-mill-ink">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-mill-moss" /> “{q}”
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Parked reference */}
        <section className="pb-4" data-testid="parked-note">
          <Link to="/reference" className="inline-flex items-center gap-1.5 text-sm font-semibold text-mill-muted hover:text-mill-ink" data-testid="reference-link">
            Future journeys & design components — not part of the core proof <ArrowUpRight className="h-4 w-4" />
          </Link>
        </section>
      </div>
    </div>
  );
}

function Head({ eyebrow, title }) {
  return (
    <div className="mb-6">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-mill-moss">{eyebrow}</p>
      <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-mill-ink sm:text-3xl">{title}</h2>
    </div>
  );
}
function QA({ q, a }) {
  return <div><dt className="text-[11px] font-semibold uppercase tracking-wider text-mill-muted">{q}</dt><dd className="text-mill-ink">{a}</dd></div>;
}
